import type { RampSpec } from '@/config/cities';

/**
 * ramp.ts — PURE launch-ramp math (no three.js, no DOM). A centred incline that
 * rises to `height` at its top lip: a body taking the middle of the road rides up
 * it and launches off the lip with upward velocity proportional to its forward
 * speed. Bodies outside the ramp's lateral half-width (passing on either side) are
 * untouched. The race applies this to every car + ball; the visual "red stairs"
 * (Scenery) are built to match. Deterministic + unit-testable.
 */

/**
 * Ramp surface height ABOVE the local ground at a track position (`distance` =
 * arc-length, `lateral` = signed offset). 0 when off the ramp (past the lip, before
 * the foot, or beyond the side gaps).
 */
export function rampHeight(
  ramp: RampSpec,
  startDistance: number,
  distance: number,
  lateral: number,
): number {
  if (Math.abs(lateral) > ramp.halfWidth) return 0;
  const t = (distance - startDistance) / ramp.length;
  if (t <= 0 || t >= 1) return 0;
  return t * ramp.height;
}

/** Launch slope (rise / run): vy gained = forwardSpeed * slope while climbing. */
export function rampSlope(ramp: RampSpec): number {
  return ramp.height / ramp.length;
}

/**
 * Ride a body up the ramp: lift it onto the incline surface and give it the
 * upward launch velocity it would gain climbing at `forwardSpeed`. Mutates `y`
 * and `vy` in place. Call only when the body is on the ramp (with `surfaceY` =
 * ground + rampHeight, plus the ball's radius for a ball).
 */
export function rideRamp(
  body: { y: number; vy: number },
  surfaceY: number,
  forwardSpeed: number,
  slope: number,
): void {
  if (body.y < surfaceY) body.y = surfaceY;
  const climb = Math.max(0, forwardSpeed) * slope;
  if (body.vy < climb) body.vy = climb;
}
